import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import "./ClientLayout.css";
import {
  CodeXml,
  Film,
  LayoutDashboard,
  Plus,
  Settings,
  Menu,
  X,
  MegaphoneIcon,
  User,
  Lock,
  LogOut,
  QrCode,
  Grid2X2,
  Users,
  Building2,
  Megaphone,
  Crown,
} from "lucide-react";

import logo from "../../src/images/ctv_logo.png";
import { getUser } from "../api/auth";
import api from "../api/axios";

const organizationMenu = [
  {
    title: "Dashboard",
    path: "/organization",
    icon: LayoutDashboard,
    tooltip: "Dashboard",
  },
  {
    title: "Brands",
    path: "/organization/brands",
    icon: Building2,
    tooltip: "Brands",
  },
  {
    title: "Users",
    path: "/organization/users",
    icon: Users,
    tooltip: "Users",
  },
  {
    title: "Templates",
    path: "/organization/templates",
    icon: Grid2X2,
    tooltip: "Templates",
  },
  {
    title: "Creatives",
    path: "/organization/creatives",
    icon: Film,
    tooltip: "Creatives",
  },
  {
    title: "Campaigns",
    path: "/organization/campaigns",
    icon: Megaphone,
    tooltip: "Campaigns",
  },
  {
    title: "XML Tester",
    path: "/organization/xml-tester",
    icon: CodeXml,
    tooltip: "XML Tester",
  },
  {
    title: "QR Code",
    path: "/organization/qr-generator",
    icon: QrCode,
    tooltip: "Dynamic QR Code",
  },
];

const userMenu = [
  {
    title: "Dashboard",
    path: "/user",
    icon: LayoutDashboard,
    permission: "Dashboard",
    tooltip: "Dashboard",
  },
  {
    title: "Brands",
    path: "/user/brands",
    icon: Building2,
    permission: "Brands",
    tooltip: "Brands",
  },
  {
    title: "Templates",
    path: "/user/templates",
    permission: "Templates",
    icon: Grid2X2,
    tooltip: "Templates",
  },
  {
    title: "Creatives",
    path: "/user/creatives",
    permission: "Creatives",
    icon: Film,
    tooltip: "Creatives",
  },
  {
    title: "Campaigns",
    path: "/user/campaigns",
    permission: "Campaigns",
    icon: Megaphone,
    tooltip: "Campaigns",
  },
  {
    title: "XML Tester",
    path: "/user/xml-tester",
    icon: CodeXml,
    tooltip: "XML Tester",
  },
  {
    title: "QR Code",
    path: "/user/qr-generator",
    icon: QrCode,
    tooltip: "Dynamic QR Code",
  },
];

export default function ClientLayout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [sidebar, setSidebar] = useState([]);

  const dropdownRef = useRef();
  const navigate = useNavigate();

  const userDetail = getUser();

  const isActive = (path) => location.pathname === path;

  const closeSidebar = () => setSidebarOpen(false);

  const redirectPath = () => {
    const userDetail = getUser();
    const role = userDetail?.role || "user";

    if (role == "admin") {
      return "admin";
    } else if (role == "organization") {
      return "organization";
    } else if (role == "user") {
      return "user";
    }
  };

  const getSubscriptionUsage = async (org_id) => {
    try {
      const response = await api.get("/subscription/usage/" + org_id);
      if (response && response.data && response.data.success) {
        setPlan(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching subscription usage:", err);
      throw err;
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (
      userDetail &&
      userDetail?.org_id &&
      userDetail?.role == "organization"
    ) {
      getSubscriptionUsage(userDetail.org_id);
    }

    const rPath = redirectPath();
    if (rPath == "organization") {
      setSidebar(organizationMenu);
    } else if (rPath == "user") {
      const permissions = userDetail?.permissions || [];

      const filteredMenu = userMenu.filter((menu) => {
        if (!menu.permission) return true; // Always show

        const permission = permissions.find(
          (p) => p.per_name === menu.permission,
        );

        return permission?.per_view === true;
      });

      setSidebar(filteredMenu);
    }

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    // Clear auth here
    localStorage.clear();
    navigate("/");
  };

  const handleClick = () => {
    navigate(userDetail ? "/organization" : "/");
    closeSidebar();
  };

  return (
    <div className="dashboard-layout">
      {/* Overlay */}
      {sidebarOpen && (
        <div className="dashboard-overlay" onClick={closeSidebar} />
      )}

      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? "open" : ""}`}>
        {/* Mobile Close Button */}
        <button className="sidebar-close-btn" onClick={closeSidebar}>
          <X size={20} />
        </button>

        <div
          onClick={() => handleClick()}
          className="dashboard-logo"
          // onClick={closeSidebar}
        >
          <span className="dashboard-logo-icon cursor-pointer">
            {/* <Tv className="h-8 w-8 text-primary" /> */}
            <img
              src={logo}
              alt="logo"
              className="h-12 w-12 text-black transform rotate-[180deg]"
            />
          </span>
          <span className="dashboard-logo-text">SocialTV</span>
        </div>

        <Link
          to={`/${redirectPath()}/editor`}
          className={`dashboard-btn-new has-tooltip tooltip-right ${isActive(`/${redirectPath()}/editor`) ? "active" : ""}`}
          data-tooltip="New Creative"
          onClick={closeSidebar}
        >
          <span className="dashboard-btn-plus">
            <Plus className="h-4 w-4" />
          </span>
          <span className="dashboard-btn-new-label">New Creative</span>
        </Link>

        <nav className="dashboard-nav">
          {sidebar.map((item) => {
            const Icon = item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`dashboard-nav-item has-tooltip tooltip-right ${
                  isActive(item.path) ? "active" : ""
                }`}
                data-tooltip={item.tooltip}
                onClick={closeSidebar}
              >
                <span className="dashboard-nav-icon">
                  <Icon size={20} />
                </span>

                <span className="dashboard-nav-label">{item.title}</span>
              </Link>
            );
          })}
        </nav>

        {userDetail && userDetail?.user_name && (
          <div className="dashboard-nav-bottom">
            <div className="dashboard-sidebar-user" ref={dropdownRef}>
              <button
                type="button"
                className="dashboard-sidebar-user-trigger has-tooltip tooltip-right"
                data-tooltip="Account"
                onClick={() => setOpen(!open)}
              >
                <div className="dashboard-sidebar-user-avatar">
                  {userDetail?.user_name
                    ?.split(" ")
                    .map((word) => word.charAt(0))
                    .join("")}
                </div>
              </button>
              {plan?.plan && <div className="planName">{plan?.plan || ""}</div>}

              {open && (
                <div className="dashboard-sidebar-dropdown">
                  {redirectPath() == "organization" && (
                    <button
                      onClick={() => {
                        navigate(`/${redirectPath()}/subscription`);
                        setOpen(false);
                        closeSidebar();
                      }}
                      className="dashboard-sidebar-dropdown-btn"
                    >
                      <Crown size={18} />
                      Subscription
                    </button>
                  )}

                  <button
                    onClick={() => {
                      navigate(`/${redirectPath()}/profile`);
                      setOpen(false);
                      closeSidebar();
                    }}
                    className="dashboard-sidebar-dropdown-btn"
                  >
                    <User size={18} />
                    Profile
                  </button>

                  <button
                    onClick={() => {
                      navigate(`/${redirectPath()}/change-password`);
                      setOpen(false);
                      closeSidebar();
                    }}
                    className="dashboard-sidebar-dropdown-btn"
                  >
                    <Lock size={18} />
                    Change Password
                  </button>

                  <hr className="dashboard-sidebar-dropdown-hr" />

                  <button
                    onClick={() => {
                      handleLogout();
                      setOpen(false);
                      closeSidebar();
                    }}
                    className="dashboard-sidebar-dropdown-btn dashboard-sidebar-dropdown-btn-danger"
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                </div>
              )}
            </div>

            {/* <Link
            to="/settings"
            className="dashboard-nav-item has-tooltip"
            data-tooltip="Settings"
            onClick={closeSidebar}
          >
            <span className="dashboard-nav-avatar">
              <Settings />
            </span>
            <span className="dashboard-nav-label">Settings</span>
          </Link> */}
          </div>
        )}
      </aside>

      {/* Mobile Menu Button (no top header) */}
      <button
        className="menu-btn-fixed"
        onClick={() => setSidebarOpen(true)}
        type="button"
        aria-label="Open menu"
      >
        <Menu />
      </button>

      {/* Main */}
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="dashboard-body">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
